/**
 * MyEvaluationsGuide: the visual guide of My evaluations (/my-evaluations), opened by the "?" after the page's title
 * (HelpTitle helpId="my_evaluations"). The page lists the questions the user voted on (For / Against) with the vote,
 * its date, the comment and the question's current status.
 * It follows the reader's role the way the question page does (QuestionPage/Main.js): only those with vote_question
 * and without approve_question get the vote form (on questions outside folders), so only they get its step and what
 * happens to a voted question. The subjects' experts, moderators and admins like or dislike others' questions with
 * the question card's VoteBar (its votes land here too; a question of the reader's review task is named above its
 * buttons). Only a subject's moderators and admins decide on a question's status there (canDecideIn: since 2026-10-29
 * its experts only vote), as approvers do outside folders (a comment needed only to approve): DecisionPanel's choice,
 * then the comment, for an approval its four checks, and the confirmation; their steps show the decision and where
 * it is kept, the question's history, since this page never lists it. The list and its
 * filters are explained to those who gather evaluations: voters, the subjects' reviewers, and anyone with one (the
 * page shows its filters from the first one on). Phones: the list's cards under 768px, the filters under the
 * "Filters" toggle under 640px (the page's own breakpoints).
 * Props, from the page: `seesExperts` (whether the reader opens /evaluators; its tip shows only then),
 * `hasEvaluations` (whether the reader has an evaluation yet).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  // The vote bar's buttons, outlined as on the question page.
  HandThumbDownIcon as DislikeIcon,
  HandThumbUpIcon as LikeIcon,
  // The voter's role pill, outlined like the other roles' (the solid thumbs are the votes').
  HandThumbUpIcon as VoterIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniChip,
  MiniDecision,
  MiniField,
  MiniIcon,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTable,
  MiniText,
} from "../Mini";
import { statusText } from "../../QuestionList/questionStatus";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canDecideIn, canReviewIn, isAdminUser } from "../../../utils/folders";

// What an approval asks to tick first (DecisionPanel's APPROVE_CHECKS, in its order).
const APPROVE_CHECKS = ["text", "answer", "topic", "level"];

export default function MyEvaluationsGuide({ seesExperts = false, hasEvaluations = false }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The page's own breakpoints: the list's rows or cards at md, FilterPanel at sm.
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.my_evaluations.example.${key}`);
  const status = (code) => statusText(t, code);

  const isAdmin = isAdminUser(user);
  const perms = user?.perms || [];
  // The question page's rule (Main.js): the vote form is for vote_question without approve_question.
  const votes = perms.includes("vote_question") && !perms.includes("approve_question");
  // The subjects' experts, moderators and admins like or dislike others' questions there (the vote bar); only its
  // moderators and admins decide on them (its experts only vote); approvers outside folders decide only. Experts and
  // moderators may have a review task (the vote bar names it).
  const roles = user?.folder_roles || [];
  const inSubjects = isAdmin || roles.some(canReviewIn);
  const decidesIn = isAdmin || roles.some(canDecideIn);
  const decides = decidesIn || perms.includes("approve_question");
  const getsTasks = roles.includes("expert") || roles.includes("moderator");
  // The list and its filters: those who vote or like get them with their first evaluation; others only if they have
  // one.
  const hasList = votes || inSubjects || hasEvaluations;

  const voteUp = t("question_detail.vote.upvote");
  const voteDown = t("question_detail.vote.downvote");
  const voteBlock = t("question_detail.vote_bar.title");
  const like = t("question_detail.vote_bar.like");
  const dislike = t("question_detail.vote_bar.dislike");
  const commentOpen = t("question_detail.vote_bar.comment_open");
  const up = t("evaluators.upvoted");
  const down = t("evaluators.downvoted");
  const filters = t("question_list.filters_short");
  const panel = t("question_detail.decision.title");
  const history = t("question_detail.history.title");
  const transition = (key) => t(`question_detail.transitions.${key}`);
  const take = transition("under_review");
  const approve = transition("approved");
  const changes = transition("changes_requested");
  const decline = transition("declined");
  // The decision panel's choices in its order (the server's transitions): buttons in the colour of the status each
  // leads to. In a subject a waiting question can also be taken for review; outside folders the old order.
  const choices = decidesIn
    ? [
        { key: "take", code: "UNDER_REVIEW", label: take },
        { key: "approve", code: "APPROVED", label: approve },
        { key: "changes", code: "CHANGES_REQUESTED", label: changes },
        { key: "decline", code: "DECLINED", label: decline },
      ]
    : [
        { key: "approve", code: "APPROVED", label: approve },
        { key: "decline", code: "DECLINED", label: decline },
        { key: "changes", code: "CHANGES_REQUESTED", label: changes },
      ];
  // A decision as the question's history shows it (StatusHistory): who, as what, the status it set, the comment.
  const historyRecord = (code, { comment = false, mark } = {}) => (
    <MiniCard mark={mark}>
      <MiniRow>
        <MiniText variant="strong">{t("help.evaluators.example.expert")}</MiniText>
        <MiniText variant="muted">({t("question_detail.roles.approver")})</MiniText>
        <MiniStatus code={code} />
      </MiniRow>
      {comment && <MiniText>{own("decision_comment")}</MiniText>}
    </MiniCard>
  );

  return (
    <>
      {/* The buttons the reader votes with: the vote bar's in subjects, the vote form's outside folders. */}
      <HelpIntro>
        <HelpText i18nKey="help.my_evaluations.intro" values={inSubjects ? { up: like, down: dislike } : { up: voteUp, down: voteDown }} />
      </HelpIntro>

      <HelpFlow pipeline="bts" current="review" />

      <HelpSteps>
        <HelpStep
          title={t("help.my_evaluations.steps.find.title")}
          illustration={
            <MiniScreen title={inSubjects ? `${ex("exam")} · ${ex("subject")}` : t("nav.question_bank")}>
              {/* Phones scroll the status chips sideways (nothing marked in there): the question's card is marked
                  instead, as a whole (a tap anywhere on it opens the question). */}
              <MiniRow scroll={!roomy}>
                <MiniChip selected count={12}>
                  {t("question_list.status_all")}
                </MiniChip>
                <MiniChip dot="amber" count={5} mark={roomy}>
                  {status("PENDING_REVIEW")}
                </MiniChip>
                {inSubjects ? (
                  <MiniChip dot="cyan" count={2}>
                    {status("RESUBMITTED")}
                  </MiniChip>
                ) : (
                  <MiniChip dot="blue" count={3}>
                    {status("UNDER_REVIEW")}
                  </MiniChip>
                )}
                <MiniChip dot="green" count={4}>
                  {status("APPROVED")}
                </MiniChip>
              </MiniRow>
              {wide ? (
                <MiniTable
                  columns={[{ label: t("question_list.columns.question"), width: "58%" }, t("question_list.columns.status")]}
                  rows={[ex("question_1"), ex("question_2")].map((name) => [
                    <MiniText variant="strong" truncate>
                      {name}
                    </MiniText>,
                    <MiniStatus code="PENDING_REVIEW" />,
                  ])}
                />
              ) : (
                <MiniCard mark={!roomy}>
                  <MiniText variant="strong">{ex("question_1")}</MiniText>
                  <MiniRow>
                    <MiniStatus code="PENDING_REVIEW" />
                  </MiniRow>
                </MiniCard>
              )}
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey={inSubjects ? "help.my_evaluations.steps.find.text_review" : "help.my_evaluations.steps.find.text_vote"}
            values={{
              bank: t("nav.question_bank"),
              pending: status("PENDING_REVIEW"),
              under: status("UNDER_REVIEW"),
              resubmitted: status("RESUBMITTED"),
            }}
          />
        </HelpStep>

        {votes && (
          <HelpStep
            title={t("help.my_evaluations.steps.vote.title")}
            illustration={
              <MiniCard>
                {/* MiniStack's wider spacing keeps the numbered bubbles off the line above them. */}
                <MiniStack>
                  <MiniText variant="title" truncate>
                    {t("question_detail.vote.title")}
                  </MiniText>
                  <MiniRow className="gap-y-3">
                    {/* No cursor on "1": it would cover the comment's label right under it. */}
                    <MiniBadge tone="green" icon={HandThumbUpIcon} mark={{ label: "1" }}>
                      {voteUp}
                    </MiniBadge>
                    <MiniBadge tone="red" icon={HandThumbDownIcon}>
                      {voteDown}
                    </MiniBadge>
                  </MiniRow>
                  <MiniRow>
                    <MiniField label={t("question_detail.comment.label")} placeholder={t("question_detail.comment.vote_placeholder")} className="flex-1" />
                  </MiniRow>
                  <MiniRow className="justify-end">
                    <MiniButton variant="primary" mark="2">
                      {t("question_detail.vote.submit")}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              </MiniCard>
            }
          >
            <HelpText
              i18nKey="help.my_evaluations.steps.vote.text"
              values={{ block: t("question_detail.vote.title"), up: voteUp, down: voteDown, submit: t("question_detail.vote.submit") }}
            />
          </HelpStep>
        )}

        {/* The question card's vote bar: the like (1) and its comment (2); above them, the note of a question of the
            reader's review task. */}
        {inSubjects && (
          <HelpStep
            title={t("help.my_evaluations.steps.like.title")}
            illustration={
              <MiniCard>
                {getsTasks && (
                  <MiniCard tone="blue" icon={ClipboardDocumentCheckIcon}>
                    {t("question_detail.vote_bar.task_todo", { task: t("folders.tasks.review_title", { count: 2 }) })}
                  </MiniCard>
                )}
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="strong">{voteBlock}</MiniText>
                  <MiniRow>
                    {/* No cursor on "1": it would cover the hint right under it. */}
                    <MiniButton icon={LikeIcon} mark={{ label: "1" }}>
                      {like}
                    </MiniButton>
                    <MiniButton icon={DislikeIcon}>{dislike}</MiniButton>
                  </MiniRow>
                </MiniRow>
                <MiniText variant="muted">{t("question_detail.vote_bar.hint")}</MiniText>
                <MiniRow>
                  <MiniButton variant="link" icon={ChatBubbleLeftEllipsisIcon} mark="2">
                    {commentOpen}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
            }
          >
            <HelpText i18nKey="help.my_evaluations.steps.like.text" values={{ block: voteBlock, like, dislike, up, down }} />
            <p>
              <HelpText i18nKey="help.my_evaluations.steps.like.text_comment" values={{ comment: commentOpen }} />
            </p>
            {getsTasks && (
              <p>
                <HelpText i18nKey="help.my_evaluations.steps.like.text_task" values={{ tasks: t("nav.my_tasks") }} />
              </p>
            )}
          </HelpStep>
        )}

        {decides && (
          <HelpStep
            title={t("help.my_evaluations.steps.decide.title")}
            illustration={
              <MiniCard>
                <MiniStack>
                  <MiniStack tight>
                    <MiniText variant="title" truncate>
                      {panel}
                    </MiniText>
                    <MiniText variant="muted" truncate>
                      {t("question_detail.decision.select")}
                    </MiniText>
                  </MiniStack>
                  <MiniRow className="gap-y-3">
                    {choices.map((choice) => (
                      <MiniDecision key={choice.key} code={choice.code} selected={choice.key === "approve"} mark={choice.key === "approve"}>
                        {choice.label}
                      </MiniDecision>
                    ))}
                  </MiniRow>
                </MiniStack>
              </MiniCard>
            }
          >
            <HelpText i18nKey="help.my_evaluations.steps.decide.text" values={{ panel, approve, changes, decline }} />
            {decidesIn && (
              <p>
                <HelpText i18nKey="help.my_evaluations.steps.decide.text_take" values={{ take }} />
              </p>
            )}
          </HelpStep>
        )}

        {decides && (
          <HelpStep
            title={t("help.my_evaluations.steps.confirm.title")}
            illustration={
              // The approval chosen in the step before: its four checks, its reason (required) and the button that
              // names it, which waits for both.
              <MiniCard>
                <MiniStack>
                  <MiniText variant="title" truncate>
                    {panel}
                  </MiniText>
                  <MiniRow>
                    <MiniDecision code="APPROVED" selected>
                      {approve}
                    </MiniDecision>
                  </MiniRow>
                  <MiniCard tone="green">
                    <MiniText variant="strong">{t("question_detail.review.approve.checks_title")}</MiniText>
                    {APPROVE_CHECKS.map((key) => (
                      <MiniRow key={key} className="flex-nowrap">
                        <MiniCheck checked />
                        <MiniText>{t(`question_detail.review.approve.check_${key}`)}</MiniText>
                      </MiniRow>
                    ))}
                  </MiniCard>
                  <MiniRow>
                    <MiniField label={t("question_detail.review.approve.reason_label")} value={own("approve_reason")} className="flex-1" />
                  </MiniRow>
                  <MiniRow className="justify-end">
                    <MiniButton variant="primary" mark>
                      {t("question_detail.review.confirm.approved")}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              </MiniCard>
            }
          >
            <HelpText
              i18nKey={decidesIn ? "help.my_evaluations.steps.confirm.text" : "help.my_evaluations.steps.confirm.text_outside"}
              values={{ approve, changes, decline }}
            />
          </HelpStep>
        )}

        {decides && (
          <HelpStep
            title={t("help.my_evaluations.steps.history.title")}
            illustration={
              // Newest first: the return with its comment; in a subject, the taking for review before it.
              <MiniScreen title={history}>
                {historyRecord("CHANGES_REQUESTED", { comment: true, mark: { pointer: false } })}
                {decidesIn && historyRecord("UNDER_REVIEW")}
              </MiniScreen>
            }
          >
            <HelpText i18nKey="help.my_evaluations.steps.history.text" values={{ history }} />
            <p>
              <HelpText i18nKey="help.my_evaluations.steps.history.text_page" values={{ up, down }} />
            </p>
          </HelpStep>
        )}

        {hasList && (
          <HelpStep
            title={t("help.my_evaluations.steps.here.title")}
            illustration={
              <MiniScreen title={t("evaluators.my_evaluations.title")}>
                {/* A click anywhere on the row opens the question (the name is its link): the row is marked as a
                    whole, so the marker leaves the name's new-tab icon in sight. */}
                <MiniCard mark>
                  <MiniRow className="gap-y-3">
                    <MiniRow className="flex-nowrap">
                      <MiniText variant="strong">{ex("question_2")}</MiniText>
                      <MiniIcon icon={ArrowTopRightOnSquareIcon} />
                    </MiniRow>
                    <MiniStatus code="UNDER_REVIEW" />
                  </MiniRow>
                  <MiniRow>
                    <MiniBadge tone="green" icon={HandThumbUpIcon}>
                      {up}
                    </MiniBadge>
                    <MiniIcon icon={ClockIcon} />
                    <MiniText variant="muted">{own("date")}</MiniText>
                  </MiniRow>
                  <MiniText>{own("comment")}</MiniText>
                </MiniCard>
              </MiniScreen>
            }
          >
            {/* A paragraph of its own (the text has no pill), so the next one keeps its space. */}
            <p>
              <HelpText i18nKey="help.my_evaluations.steps.here.text" />
            </p>
            {(votes || inSubjects) && <p>{t("help.my_evaluations.steps.here.text_change")}</p>}
          </HelpStep>
        )}

        {hasList && (
          <HelpStep
            title={t("help.my_evaluations.steps.filter.title")}
            illustration={
              <MiniStack>
                <MiniRow className="gap-y-3">
                  <MiniChip selected>{t("question_list.status_all")}</MiniChip>
                  <MiniChip dot="green">{up}</MiniChip>
                  <MiniChip dot="red" mark>
                    {down}
                  </MiniChip>
                </MiniRow>
                <MiniCard>
                  {roomy ? (
                    <MiniRow>
                      <MiniField icon={MagnifyingGlassIcon} placeholder={t("evaluators.my_evaluations.search_placeholder")} className="flex-1" />
                      <MiniField select placeholder={t("exam_taking.all_statuses")} />
                    </MiniRow>
                  ) : (
                    <MiniRow className="flex-nowrap">
                      <MiniField
                        icon={MagnifyingGlassIcon}
                        placeholder={t("evaluators.my_evaluations.search_placeholder_short")}
                        className="flex-1"
                      />
                      <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} chevron>
                        {filters}
                      </MiniButton>
                    </MiniRow>
                  )}
                </MiniCard>
              </MiniStack>
            }
          >
            <HelpText i18nKey="help.my_evaluations.steps.filter.text" values={{ up, down }} />
            <p>
              {roomy ? (
                <HelpText i18nKey="help.my_evaluations.steps.filter.search" values={{ statuses: t("exam_taking.all_statuses") }} />
              ) : (
                <HelpText i18nKey="help.my_evaluations.steps.filter.search_phone" values={{ filters }} />
              )}
            </p>
            {/* Until the first evaluation the page shows its empty state alone. */}
            {!hasEvaluations && <p>{t("help.my_evaluations.steps.filter.text_later")}</p>}
          </HelpStep>
        )}

        {votes && (
          <HelpStep
            title={t("help.my_evaluations.steps.next.title")}
            illustration={
              <MiniRow className="gap-y-3">
                <MiniStatus code="PENDING_REVIEW" />
                <MiniIcon icon={ArrowRightIcon} />
                <MiniStatus code="UNDER_REVIEW" mark={{ pointer: false }} />
                <MiniIcon icon={ArrowRightIcon} />
                <MiniStack tight>
                  {["APPROVED", "CHANGES_REQUESTED", "DECLINED"].map((code) => (
                    <MiniRow key={code}>
                      <MiniStatus code={code} />
                    </MiniRow>
                  ))}
                </MiniStack>
              </MiniRow>
            }
          >
            <HelpText
              i18nKey="help.my_evaluations.steps.next.text"
              values={{ pending: status("PENDING_REVIEW"), under: status("UNDER_REVIEW") }}
            />
          </HelpStep>
        )}
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "voter", role: t("help.my_evaluations.roles.voter_label"), Icon: VoterIcon, can: t("help.my_evaluations.roles.voter") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.my_evaluations.roles.expert") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.my_evaluations.roles.moderator") },
          {
            key: "admin",
            role: t("folders.roles.admin"),
            Icon: KeyIcon,
            can: <HelpText i18nKey="help.my_evaluations.roles.admin" values={{ experts: t("nav.evaluators") }} />,
          },
        ]}
      />

      <HelpTips
        items={[
          (votes || inSubjects) && t("help.my_evaluations.tips.one_vote"),
          // The filters card's "Clear all" is on the page once there is a list to filter.
          hasEvaluations && <HelpText key="clear" i18nKey="help.my_evaluations.tips.clear" values={{ clear: t("question_list.clear_all") }} />,
          // Those who decide have the history as a step of their own.
          !decides && <HelpText key="history" i18nKey="help.my_evaluations.tips.history" values={{ history }} />,
          // Nobody but an admin votes on their own question in a subject; its moderator's own goes to another moderator
          // or an admin for the decision (the decision panel says so there).
          inSubjects && !isAdmin && t("help.my_evaluations.tips.own"),
          (decides || inSubjects) && (
            <HelpText key="guide" i18nKey="help.my_evaluations.tips.guide" values={{ guide: t("help.review.title") }} />
          ),
          seesExperts && <HelpText key="experts" i18nKey="help.my_evaluations.tips.experts" values={{ experts: t("nav.evaluators") }} />,
        ]}
      />
    </>
  );
}
